import { type ReactNode } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

import { Image } from '../../image/src/image';
import { Link } from '../../link/src/link';
import { Lozenge, type LozengeAppearance } from '../../lozenge/src/lozenge';

export interface BlogNewsCardProps {
	color: BlogNewsCardColor;
	label: string;
	title: string;
	link: {
		href: string;
		text: string;
		dataEventProps?: DataEventProps;
	};
	image: {
		src: string;
		alt: string;
	};
	/** Slot rendered between the lozenge and the title (used by BlogEventCard). */
	eventMeta?: ReactNode;
	motionConfig?: {
		transformForward: string;
		transformBackward: string;
	};
}

export type BlogNewsCardColor = 'purple' | 'green' | 'orange' | 'blue';

export const BlogNewsCard = (props: BlogNewsCardProps) => {
	const { color, label, title, link, image, eventMeta, motionConfig } = props;

	const bgColor = colorToToken[color];
	const ariaLabel = `${link.text}: ${title}`;

	return (
		<div
			css={[
				cardStyles,
				motionConfig?.transformForward &&
					css({
						transform: motionConfig?.transformForward,
						['@container style(--gc-scroll-direction <= 0)']: {
							transform: motionConfig?.transformBackward,
						},
					}),
			]}
		>
			<div
				css={imageWrapperStyles}
				className="blog-news-card-media"
				style={{ backgroundColor: bgColor }}
			>
				<Image src={image.src} alt={image.alt} className="blog-news-card-image" />
			</div>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
			<div css={contentStyles} style={{ backgroundColor: bgColor }}>
				<Lozenge appearance={colorToLozenge[color]} text={label} />
				{eventMeta}
				<div css={titleStyles} className="card-title-sm">
					{title}
				</div>
				<Link
					type="primary"
					bgColor="bgBlackBlue"
					href={link.href}
					text={link.text}
					aria-label={ariaLabel}
					ariaLabel={ariaLabel}
					className="blog-news-card-link"
					dataEventProps={link.dataEventProps}
				/>
			</div>
		</div>
	);
};

const colorToToken: Record<BlogNewsCardColor, string> = {
	purple: wacToken('color.extended.purple800'),
	green: wacToken('color.extended.lime800'),
	orange: wacToken('color.extended.orange700'),
	blue: wacToken('color.extended.blue800'),
};

const colorToLozenge: Record<BlogNewsCardColor, LozengeAppearance> = {
	purple: 'purple',
	green: 'lime',
	orange: 'saffron',
	blue: 'blue',
};

const cardStyles = css({
	borderRadius: wacToken('corner.radius.card'),
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	textDecoration: 'none',
	color: 'inherit',
	flexShrink: 0,
	position: 'relative',
	[media.above.sm]: {
		width: '200px',
	},
	[media.above.md]: {
		width: '268px',
	},
	[media.above.lg]: {
		width: '388px',
	},
	width: '280px',
	height: 'fit-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover .blog-news-card-image': {
		transform: 'scale(1.1)',
		transitionTimingFunction: wacToken('motion.easing.ease40-out'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover .blog-news-card-media::after': {
		transform: 'translateY(0)',
		transitionTimingFunction: wacToken('motion.easing.ease40-out'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.blog-news-card-link::after': {
		content: '""',
		position: 'absolute',
		inset: 0,
	},
	// animation hook when in a gallery container with extra motion enabled
	willChange: 'transform',
	transform: `translateX(calc(var(--gc-scroll-speed) * var(--gc-item-i, 0) / 9 * 20rem))`,
	['@container style(--gc-scroll-direction <= 0)']: {
		transform: `translateX(calc(var(--gc-scroll-speed) * (9 - var(--gc-item-i, 0)) / 9 * -20rem))`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.blog-news-card-link': {
		[media.above.lg]: {
			fontSize: '20px',
		},
	},
});

const imageWrapperStyles = css({
	overflow: 'hidden',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.blog-news-card-image': {
		width: '100%',
		aspectRatio: '1 / 1',
		objectFit: 'cover',
		display: 'block',
		transition: `transform ${wacToken('motion.delay.250ms')} ${wacToken(
			'motion.easing.ease60-in',
		)}`,
	},
	'&::after': {
		content: '""',
		backgroundColor: 'inherit',
		position: 'absolute',
		insetBlockEnd: 0,
		insetInline: 0,
		height: token('space.150'),
		transform: `translateY(${token('space.150')})`,
		zIndex: 0,
		transition: `transform ${wacToken('motion.delay.250ms')} ${wacToken(
			'motion.easing.ease60-in',
		)}`,
	},
});

const contentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.250'),
	padding: `${token('space.300')} ${token('space.200')}`,
	[media.above.sm]: {
		padding: `${token('space.250')}`,
	},
	[media.above.md]: {
		padding: token('space.300'),
	},
	[media.above.xl]: {
		padding: token('space.400'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a:link, a:visited, a:hover, a:active': {
		color: `${wacToken('color.neutral.light0')} !important`,
	},
});

const titleStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div&.card-title-sm': {
		color: wacToken('color.neutral.light0'),
	},
});
